<template>
    <b-modal id="discharge_form" ref="discharge_form"
             @hidden="resetForm" no-close-on-backdrop
             modal-class="modal-right modal-dialog-scrollable">

        <template v-slot:modal-title>
             Discharge Client
        </template>

        <b-form v-if="admission">
            <b-row>
                <b-colxx md="12">
                    <b-alert show variant="info">
                        Code: {{ admission.data.attributes.code }} <br>
                        Facility: {{ admission.data.attributes.facility }} <br>
                        Unit: {{ admission.data.attributes.unit }} <br>
                        Admission Date: {{ admission.data.attributes.move_in }} <br>
                        Client's Name: {{ admission.data.attributes.client }} <br>
                        <b-badge pill
                                 :variant="clientStatusColor(admission.data.attributes.client_status)">
                            Client's Status: {{ admission.data.attributes.client_status }}
                        </b-badge>
                    </b-alert>
                </b-colxx>
            </b-row>

            <b-row v-if="validationError">
                <b-colxx md="12">
                    <b-alert class="text-center" show variant="danger"
                             v-text="validationError"/>
                </b-colxx>
            </b-row>

            <b-row>
                <b-colxx md="12">
                    <b-form-group label="Move-out" class="has-float-label">
                        <template v-slot:label>
                            <span>Move-out<sup><code>*</code></sup></span>
                        </template>

                        <v-date-picker
                            popover-visibility="focus" mode="single"
                            v-model="datepicker.move_out"
                            @input="'move_out' in errors ? delete errors.move_out : ''"
                            :input-props="{ class:'form-control' + ('move_out' in errors ? ' border-danger' : ''), placeholder: 'MM-DD-YYYY' }"/>

                        <b-form-invalid-feedback :state="!'move_out' in errors"
                                                 v-text="'move_out' in errors ? errors.move_out[0] : ''"/>
                    </b-form-group>
                </b-colxx>
            </b-row>

            <b-row class="mb-4">
                <b-colxx md="12">
                    <vue-dropzone ref="fileDropZone" id="dropzone"
                                  @vdropzone-error="attachmentError"
                                  :destroy-dropzone="false" :options="dropzoneOptions"/>
                </b-colxx>
            </b-row>

            <b-row>
                <b-colxx md="12" class="mb-4">
                    <b-form-group label="Notes" class="has-float-label">
                        <b-form-textarea v-model="form.notes" rows="6" placeholder="Notes"/>
                    </b-form-group>
                </b-colxx>
            </b-row>
        </b-form>
        <template slot="modal-footer">
            <b-button variant="outline-secondary" @click="hideModal('discharge_form')" v-text="'Cancel'"/>

            <state-button variant="primary" :click="submitDischarge">Submit</state-button>
        </template>
    </b-modal>
</template>

<script>
import VueDropzone from "vue2-dropzone";
import {mapGetters} from "vuex";
import StateButton from "../../../../../components/Common/StateButton";
import moment from "moment";


export default {
    name: "DischargeFormModal",

    components: {
        VueDropzone,
        StateButton
    },

    props: [
        'admission'
    ],

    data() {
        return {
            datepicker: {
                move_out: null,
                ref_date: null,
            },
            dropzoneOptions: {
                url: 'url',
                thumbnailHeight: 160,
                acceptedFiles: '.jpg,.jpeg,.png,.pdf',
                maxFilesize: 10,
                previewTemplate: this.dropzoneTemplate(),
                headers: {
                    'Authorization': 'Bearer ' + this.$store.getters.accessToken
                },
                dictDefaultMessage: 'Drop or Click here to upload files.<br>Max file size 10MB.<br>Accepted file types: JPG, JPEG, PNG, PDF.',
                autoProcessQueue: false
            },
            validationError: null,
        }
    },

    computed: {
        ...mapGetters({
            form: 'getDischargeForm',
            errors: 'getDischargeErrors',
        })
    },

    watch: {
        'datepicker.move_out'(date) {
            if (moment(date).format('YYYY-MM-DD') === 'Invalid date') return this.form.move_out = null;
            this.form.move_out = moment(date).format('YYYY-MM-DD');
        },
    },

    methods: {
        resetForm() {
            this.$store.commit('setDischargeForm');

            this.validationError = null;

            this.$emit('closed');
        },

        attachmentError(file, message, xhr) {
            this.$refs.fileDropZone.removeFile(file);
            this.$notify('error filled', 'Error!', file.name + " could not be added!", {
                duration: 6000,
                permanent: false
            });
        },

        hideModal(refname) {
            this.$refs[refname].hide();
        },

        uploadFiles(admission) {
            let url = '/api/hms/clients/' + admission.data.attributes.client_id + '/attachments';

            this.$refs.fileDropZone.setOption('url', url);

            this.$refs.fileDropZone.processQueue(admission);
        },

        submitDischarge() {
            let payload = {
                admission_id: this.admission.admission_id,
                form: {...this.form, unit_id: this.admission.data.attributes.unit_id},
            }

            return new Promise((success, fail) => {
                this.$store.dispatch('submitDischarge', payload)
                    .then(response => {
                        this.uploadFiles(response.data);
                    })
                    .then(response => {
                        setTimeout(() => {
                            success();

                            this.$emit('discharged-client');

                            this.resetForm();

                            this.$notify('error filled', 'Success!', 'Discharged client!', {
                                duration: 4000,
                                permanent: false
                            });

                            this.hideModal('discharge_form');
                        }, 2000);
                    })
                    .catch(error => {
                        if (error.response.data.error) {
                            this.validationError = error.response.data.error;
                        }

                        setTimeout(() => {
                            fail();
                        }, 1000);
                    });
            })
        },

        clientStatusColor: status => {
            if (status.toLowerCase() === 'referral') return 'warning'
            else if (status.toLowerCase() === 'admitted') return 'success'
            else if (status.toLowerCase() === 'transferred') return 'info'
            else if (status.toLowerCase() === 'discharged') return 'danger'
            else if (status.toLowerCase() === 'duplicate') return 'dark'
            else return 'light'
        },

        dropzoneTemplate() {
            return `<div class="dz-preview dz-file-preview mb-3">
                  <div class="d-flex flex-row "> <div class="p-0 w-30 position-relative">
                      <div class="dz-error-mark"><span><i></i>  </span></div>
                      <div class="dz-success-mark"><span><i></i></span></div>
                      <div class="preview-container">
                        <img data-dz-thumbnail class="img-thumbnail border-0" />
                        <i class="simple-icon-doc preview-icon"></i>
                      </div>
                  </div>
                  <div class="pl-3 pt-2 pr-2 pb-1 w-70 dz-details position-relative">
                    <div> <span data-dz-name /> </div>
                    <div class="text-primary text-extra-small" data-dz-size /> </div>
                    <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div>
                    <div class="dz-error-message"><span data-dz-errormessage></span></div>
                  </div>
                  <a href="#" class="remove" data-dz-remove> <i class="glyph-icon simple-icon-trash"></i> </a>
                </div>`
        },
    }
}
</script>

<style scoped>

</style>
